import {
    BrandType,
    BulletPointType,
    CorrespondenceType,
    CrossSellType,
    DisclaimerType,
    EnvironmentalClaimType,
    GuidelineType,
    ImageType,
    Nullable,
    NullableProductOptionType,
    NullableProductOptionValueType,
    PriceType,
    ProductOptionType,
    ProductOptionValueItemType,
    ProductResponseType,
    ResourceType,
    StockInformationType,
    VideoType,
    YouTubeVideoType
} from "../../definitions/types";
import {ArtworkGuidelineType, ItemType, PrivacyStatusType, UploadStatusType} from "../../definitions/enums";
import {DynamicDimensionsModel} from "../view/dynamicDimensionsModel";
import {defaultQuestionStatistics, defaultRatingStatistics} from "../../utils/constants";
import {RatingStatisticsModel} from "./ratingStatisticsModel";
import {QuestionStatisticsModel} from "./questionStatisticsModel";
import {YouTubeVideoModel} from "./youTubeVideoModel";
import {ImageModel} from "./imageModel";
import {GuidelineModel} from "./guidelineModel";
import {VideoModel} from "./videoModel";

/**
 * @class
 * @description - Product model class that contains all the properties of a product and methods to access them in a consistent way
 * @example
 * const product = new ProductModel(product);
 * const { price } = product;
 * const { currentPrice } = price;
 * ... etc.
 */
export class ProductModel {
    private readonly _product: Nullable<ProductResponseType>;

    /**
     * @constructor
     * @param product - ProductResponseType
     * @throws Error - if product is null
     * @returns ProductModel
     * @description - Creates an instance of Product class from ProductResponseType object and returns it if product is not null
     */
    constructor(public readonly product: Nullable<ProductResponseType>) {
        this._product = product;
    }

    /**
     * @method id
     * @description - Returns the product id or undefined if product is null or id is null or undefined
     * @returns {number | undefined}
     */
    public get id(): number | undefined {
        const {id} = this._product ?? {id: undefined};
        return id;
    }

    /**
     * @method
     * @returns { articles: CorrespondenceType[]; guides: CorrespondenceType[] }
     * @description - Returns correspondence articles and guides or empty arrays if articles or guides are null
     * @see
     * @example
     * {
     *  articles: [
     *    {
     *     id: 1,
     *     name: 'article1'
     *    },
     *    {
     *     id: 2,
     *     name: 'article2'
     *    }
     *    ],
     *     guides: [
     *    {
     *     id: 1,
     *     name: 'guide1'
     *    },
     *    {
     *     id: 2,
     *     name: 'guide2'
     *    }
     *  ]
     */
    public get correspondence(): { guides: CorrespondenceType[]; articles: CorrespondenceType[] } {
        const {articles, primaryArticles, guides, primaryGuides} = this._product ?? {
            articles: [] as Nullable<CorrespondenceType>[],
            primaryArticles: [] as Nullable<CorrespondenceType>[],
            guides: [] as Nullable<CorrespondenceType>[],
            primaryGuides: [] as Nullable<CorrespondenceType>[]
        };

        return {
            articles: [
                ...articles.filter((article): article is CorrespondenceType => article !== null),
                ...primaryArticles.filter((article): article is CorrespondenceType => article !== null)
            ].reduce((unique, article) => {
                if (!unique.some((uniqueArticle) => uniqueArticle.id === article.id)) unique.push(article);
                return unique;
            }, [] as CorrespondenceType[]),
            guides: [
                ...guides.filter((guide): guide is CorrespondenceType => guide !== null),
                ...primaryGuides.filter((guide): guide is CorrespondenceType => guide !== null)
            ].reduce((unique, guide) => {
                if (!unique.some((uniqueGuide) => uniqueGuide.id === guide.id)) unique.push(guide);
                return unique;
            }, [] as CorrespondenceType[])
        }
    }

    /**
     * @method
     * @returns {ArtworkGuidelineType}
     * @description - Returns ArtworkGuidelineType enum value or None if artworkGuidelineType is null
     */
    public get artworkGuidelineType(): ArtworkGuidelineType {
        const {artworkGuidelineType} = this._product ?? {artworkGuidelineType: undefined};
        return artworkGuidelineType ?? ArtworkGuidelineType.None;
    }

    /**
     * @method
     * @returns {number | undefined}
     * @description - Returns artworkGuidelineTypeId or null if artworkGuidelineTypeId is null
     */
    public get artworkGuidelineTypeId(): number | undefined {
        const {artworkGuidelineTypeId} = this._product ?? {artworkGuidelineTypeId: undefined};
        return artworkGuidelineTypeId;
    }

    /**
     * @property artworkGuidelineTypePath
     * @description - Returns undefined if artworkGuidelineTypeId is null or 6,
     * otherwise returns the path to the artwork guideline type details page
     * @returns {string | undefined}
     */
    public get artworkGuidelineTypePath(): string | undefined {
        if (!this.id || !this.artworkGuidelineTypeId || this.artworkGuidelineTypeId === 6) return undefined;
        return `/artworkguideline/${this.artworkGuidelineTypeId}/${this.id}`;
    }

    /**
     * @method
     * @returns {BrandType | undefined}
     * @description - Returns artworkGuidelineTypeName or null if artworkGuidelineTypeName is null
     */
    public get brand(): BrandType | undefined {
        const {brand} = this._product ?? {brand: undefined};
        return brand;
    }

    /**
     * @property aggregatedBulletPoints
     * @description - Returns aggregated bullet points with the variant bullet points first and the primary bullet points second
     * (Duplicate bullet points are removed from the array, ignoring case)
     * @returns {BulletPointType[]}
     */
    public get aggregatedBulletPoints(): BulletPointType[] {
        return [...this.variantBulletPoints, ...this.primaryBulletPoints].filter((bulletPoint, index, self) => index === self.findIndex((t) => t.description.trim().toLowerCase() === bulletPoint.description.trim().toLowerCase()));
    }

    /**
     * @property BulletPointType
     * @returns {BulletPointType[]}
     * @description - Return array of BulletPointType
     */
    public get variantBulletPoints(): BulletPointType[] {
        const {bulletPoints} = this._product ?? {bulletPoints: [] as Nullable<BulletPointType>[]};
        return bulletPoints.filter((bulletPoint): bulletPoint is BulletPointType => bulletPoint !== null);
    }

    /**
     * @property primaryBulletPoints
     * @returns {BulletPointType[]}
     * @description - Return array of BulletPointType
     */
    public get primaryBulletPoints(): BulletPointType[] {
        const {primaryBulletPoints} = this._product ?? {primaryBulletPoints: [] as Nullable<BulletPointType>[]};
        return primaryBulletPoints.filter((bulletPoint): bulletPoint is BulletPointType => bulletPoint !== null);
    }

    /**
     * @method
     * @returns {string | undefined}
     * @description - Returns componentDescription or null if componentDescription is null
     */
    public get componentDescription(): string | undefined {
        const {componentDescription} = this._product ?? {componentDescription: undefined};
        return componentDescription;
    }

    /**
     * @method
     * @returns {{ id: number; url: string } | null}
     * @description - Returns customizedId and customizedUrl or null, if customizedId or customizedUrl is null
     */
    public get customizedItem(): Nullable<{ id: number; itemType: ItemType, url: string, path: string }> {
        const {customizedId, customizedItemType, customizedUrl, customizedPath} = this._product ?? {
            customizedId: undefined,
            customizedItemType: undefined,
            customizedUrl: undefined,
            customizedPath: undefined
        };
        return customizedId && customizedItemType && customizedUrl && customizedPath ? {
            id: customizedId,
            itemType: customizedItemType,
            url: customizedUrl,
            path: customizedPath
        } : null;
    }

    /**
     * @method
     * @returns {DisclaimerType[]}
     * @description - Return array of DisclaimerType or empty array if disclaimers are null
     */
    public get disclaimers(): DisclaimerType[] {
        const {disclaimers} = this._product ?? {disclaimers: [] as Nullable<DisclaimerType>[]};
        return disclaimers.filter((disclaimer): disclaimer is DisclaimerType => disclaimer !== null);
    }

    /**
     * @method
     * @returns {{ score: number; claims: EnvironmentalClaimType[] } | null}
     * @description - Returns environmentalScore and environmentalClaims or null, if environmentalScore or environmentalClaims is null
     * @see
     * @example
     * {
     *  isConscious: true,
     *  score: 3,
     *  claims: ['claim1', 'claim2']
     * }
     */
    public get environmental(): Nullable<{ isConscious: boolean; score: number, claims: EnvironmentalClaimType[] }> {
        const {isEnvironmentallyConscious, environmentalScore, environmentalClaims} = this._product ?? {
            isEnvironmentallyConscious: false,
            environmentalScore: null,
            environmentalClaims: [] as Nullable<EnvironmentalClaimType>[]
        };
        return environmentalScore && environmentalClaims ? {
            isConscious: isEnvironmentallyConscious,
            score: environmentalScore,
            claims: environmentalClaims.filter((claim): claim is EnvironmentalClaimType => claim !== null && claim.description?.length > 0)
        } : null;
    }

    /**
     * @method
     * @returns {boolean}
     * @description - Returns true if installationConsultation is true, otherwise false
     * @see
     * @example
     * true
     */
    public get installationConsultation(): boolean {
        const {installationConsultation} = this._product ?? {installationConsultation: false};
        return installationConsultation;
    }

    /**
     * @method
     * @returns {{ short: string | undefined; long: string | undefined }}
     * @description - Returns shortDescription and longDescription
     * @see
     * @example
     * {
     *   short: 'short description',
     *   long: 'long description'
     * }
     */
    public get descriptions(): { short: string | undefined, long: string | undefined } {
        const {shortDescription, longDescription} = this._product ?? {
            shortDescription: undefined,
            longDescription: undefined
        };
        return {
            short: shortDescription,
            long: longDescription
        }
    }

    /**
     * @method
     * @description - Returns all media or empty arrays if media is null
     * @returns { images: { raw: ImageType[]; dot4b: ImageType[] }; videos: { productIntro: VideoType[]; productSeriesShowcase: VideoType[]; productOnly: VideoType[] }; guidelines: { artworkTemplate: GuidelineType[]; instructions: GuidelineType[]; warranty: GuidelineType[] } }
     */
    public get media(): {
        images: { dot4b: ImageModel[]; raw: ImageModel[] };
        guidelines: {
            instructions: GuidelineModel[];
            artworkTemplate: GuidelineModel[];
            universal: GuidelineModel[];
            warranty: GuidelineModel[]
        };
        videos: { productSeriesShowcase: VideoModel[]; productIntro: VideoModel[]; productOnly: VideoModel[], assemblyHowTo: VideoModel[] }
    } {
        const {media} = this._product ?? {media: undefined};
        return {
            images: {
                raw: media
                    ? media.images.raw
                        .filter((image): image is ImageType => image !== null)
                        .map((image) => new ImageModel(image))
                    : [] as ImageModel[],
                dot4b: media
                    ? media.images.dot4b
                        .filter((image): image is ImageType => image !== null)
                        .map((image) => new ImageModel(image))
                    : [] as ImageModel[]
            },
            videos: {
                productIntro: media
                    ? media.videos.productIntro
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[],
                productSeriesShowcase: media
                    ? media.videos.productSeriesShowcase
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[],
                productOnly: media
                    ? media.videos.productOnly
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[],
                assemblyHowTo: media
                    ? media.videos.assemblyHowTo
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[]
            },
            guidelines: {
                artworkTemplate: media
                    ? media.guidelines.artworkTemplate
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))
                    : [] as GuidelineModel[],
                instructions: media
                    ? media.guidelines.instructions
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))
                    : [] as GuidelineModel[],
                universal: media
                    ? media.guidelines.universal
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))
                    : [] as GuidelineModel[],
                warranty: media
                    ? media.guidelines.warranty
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))
                    : [] as GuidelineModel[]
            }
        };
    }

    /**
     * @method
     * @description - Returns all primaryMedia or empty arrays if media is null
     * @returns { images: { raw: ImageType[]; dot4b: ImageType[] }; videos: { productIntro: VideoType[]; productSeriesShowcase: VideoType[]; productOnly: VideoType[] }; guidelines: { artworkTemplate: GuidelineType[]; instructions: GuidelineType[]; warranty: GuidelineType[] } }
     */
    public get primaryMedia(): {
        images: { dot4b: ImageModel[]; raw: ImageModel[] };
        guidelines: {
            instructions: GuidelineModel[];
            artworkTemplate: GuidelineModel[];
            universal: GuidelineModel[];
            warranty: GuidelineModel[]
        };
        videos: { productSeriesShowcase: VideoModel[]; productIntro: VideoModel[]; productOnly: VideoModel[], assemblyHowTo: VideoModel[] }
    } {
        const {primaryMedia} = this._product ?? {primaryMedia: undefined};
        return {
            images: {
                raw: primaryMedia
                    ? primaryMedia.images.raw
                        .filter((image): image is ImageType => image !== null)
                        .map((image) => new ImageModel(image))
                    : [] as ImageModel[],
                dot4b: primaryMedia
                    ? primaryMedia.images.dot4b
                        .filter((image): image is ImageType => image !== null)
                        .map((image) => new ImageModel(image))
                    : [] as ImageModel[]
            },
            videos: {
                productIntro: primaryMedia
                    ? primaryMedia.videos.productIntro
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[],
                productSeriesShowcase: primaryMedia
                    ? primaryMedia.videos.productSeriesShowcase
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[],
                productOnly: primaryMedia
                    ? primaryMedia.videos.productOnly
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[],
                assemblyHowTo: primaryMedia
                    ? primaryMedia.videos.assemblyHowTo
                        .filter((video): video is VideoType => video !== null)
                        .map((video) => new VideoModel(video))
                    : [] as VideoModel[]
            },
            guidelines: {
                artworkTemplate: primaryMedia
                    ? primaryMedia.guidelines.artworkTemplate
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))

                    : [] as GuidelineModel[],
                instructions: primaryMedia
                    ? primaryMedia.guidelines.instructions
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))
                    : [] as GuidelineModel[],
                universal: primaryMedia
                    ? primaryMedia.guidelines.universal
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))
                    : [] as GuidelineModel[],
                warranty: primaryMedia
                    ? primaryMedia.guidelines.warranty
                        .filter((guideline): guideline is GuidelineType => guideline !== null)
                        .map((guideline) => new GuidelineModel(guideline))
                    : [] as GuidelineModel[]
            }
        };
    }

    /**
     * @method dynamicDimensions
     * @description - Returns dynamicDimensions model
     * @returns {DynamicDimensionsModel}
     */
    public get dynamicDimensions(): DynamicDimensionsModel {
        const {
            isDynamicDimension,
            minWidth,
            maxWidth,
            minHeight,
            maxHeight
        } = this._product ?? {
            isDynamicDimension: false,
            minWidth: undefined,
            maxWidth: undefined,
            minHeight: undefined,
            maxHeight: undefined
        }

        return new DynamicDimensionsModel(isDynamicDimension, minWidth, maxWidth, minHeight, maxHeight);
    }

    /**
     * @method
     * @returns an array of ProductOptionType
     * @description - Returns options or empty array if options are null or contain null values
     */
    public get options(): ProductOptionType[] {
        const {productOptions} = this._product ?? {productOptions: [] as Nullable<NullableProductOptionType>[]};

        return productOptions
            .filter((productOption): productOption is NullableProductOptionType => productOption !== null)
            .map((productOption) => {
                return {
                    ...productOption,
                    values: productOption.values
                        .filter((productOptionValue): productOptionValue is NullableProductOptionValueType => productOptionValue !== null)
                        .map((productOptionValue) => {
                            return {
                                ...productOptionValue,
                                items: productOptionValue.items
                                    .filter((productOptionValueItem): productOptionValueItem is ProductOptionValueItemType => productOptionValueItem !== null)
                            }
                        })
                }
            });
    }

    /**
     * @method
     * @returns {PriceType | null}
     * @description - Returns price or null if price is null
     * @see
     * @example
     * {
     *  currency: 'USD',
     *  price: 100,
     *  salePrice: 90,
     *  onSale: true,
     *  ...etc (see PriceType)
     * }
     */
    public get price(): Nullable<PriceType> {
        const {price} = this._product ?? {price: null};
        return price;
    }

    /**
     * @method
     * @returns {CrossSellType[]}
     * @description - Return array of CrossSellType or empty array if replacementParts is null
     * @see
     * @example
     * [
     *  {
     *   id: 1,
     *   ...etc. (see CrossSellType)
     *   }
     *  ]
     */
    public get replacementParts(): CrossSellType[] {
        const {replacementParts} = this._product ?? {replacementParts: [] as Nullable<CrossSellType>[]};
        return replacementParts
            ? replacementParts.filter((replacementPart): replacementPart is CrossSellType => replacementPart !== null)
            : [] as CrossSellType[];
    }

    /**
     * @method
     * @returns {ResourceType[]}
     * @description - Return array of ResourceType or empty array if resources is null
     * @see
     * @example
     * [
     *  {
     *   id: 1,
     *   name: 'resource1',
     *   price: 100,
     *   ...etc. (see ResourceType)
     *  }
     * ]
     */
    public get resources(): ResourceType[] {
        const {resources} = this._product ?? {resources: [] as Nullable<ResourceType>[]};
        return resources.filter((resource): resource is ResourceType => resource !== null);
    }

    /**
     * @method warranties
     * @returns {ResourceType[]}
     * @description - Return array of ResourceType or empty array if there are no warranties in resource array or if resource is null
     */
    public get warranties(): ResourceType[] {
        return this.resources.filter(resource => resource.type === 1 || resource.type === 2);
    }

    /**
     * @method designService
     * @returns {ResourceType | undefined}
     * @description - Returns a ResourceType or undefined if there is no design service resource within the resource array
     */
    public get designService(): ResourceType | undefined {
        return this.resources.find(resource => resource.type === 0);
    }

    /**
     * @method
     * @returns { string | undefined }
     * @description - Returns SKU or undefined if SKU is null
     * @see
     * @example
     * 'SKU123'
     */
    public get sku(): string | undefined {
        const {sku} = this._product ?? {sku: undefined};
        return sku;
    }

    /**
     * @method
     * @returns Nullable<StockInformationType>
     * @description - Returns array of SpecificationType or empty array if specifications are null.
     *  Not currently in use, but added in case stock information needs to be moved here.
     */
    public get stockInformation(): null | StockInformationType {
        const {stockInformation} = this._product ?? {stockInformation: null};
        return stockInformation
    }

    /**
     * @method callOut
     * @description - Returns the product's callOut or undefined if there is no callOut associated
     * @returns {string | undefined}
     */
    public get callOut(): string | undefined {
        const {callOut} = this._product ?? {callOut: undefined};
        return callOut;
    }

    /**
     * @method ratingStatistics
     * @description - Returns the product's rating statistics or default statistics if there are no ratings
     * @returns {RatingStatisticsModel}
     */
    public get ratingStatistics(): RatingStatisticsModel {
        return new RatingStatisticsModel((this._product?.primaryRatings ?? this._product?.ratings)?.stats ?? defaultRatingStatistics);
    }

    /**
     * @method questionStatistics
     * @description - Returns the product's question statistics or default statistics if there are no questions
     * @returns {QuestionStatisticsModel}
     */
    public get questionStatistics(): QuestionStatisticsModel {
        return new QuestionStatisticsModel((this._product?.primaryQuestions ?? this._product?.questions)?.stats ?? defaultQuestionStatistics);
    }

    /**
     * @method isDesignerProduct
     * @description - Returns true if the product is a designer product
     * @returns {boolean}
     */
    public get isDesignerProduct(): boolean {
        return this._product?.isDesignerProduct ?? false;
    }

    /**
     * @method slug
     * @description - Returns the slug of the associated primary, then standalone, then undefined if none
     * @returns {string | undefined}
     */
    public get slug(): string | undefined {
        return this._product?.slug;
    }

    /**
     * @method title
     * @description - Returns the title of the associated primary, then standalone, then undefined if none
     * @returns {string | undefined}
     */
    public get title(): string | undefined {
        return this._product?.title;
    }

    /**
     * @property comparisonId
     * @description - Returns the comparisonId of the associated primary, then standalone, then undefined if none
     * @returns {number | undefined}
     */
    public get comparisonId(): number | undefined {
        return this._product?.comparisonId;
    }

    /**
     * @property youTubeVideos
     * @description - Returns the YouTube videos associated with the product
     * @returns {YouTubeVideoModel[]}
     */
    public get youTubeVideos(): YouTubeVideoModel[] {
        if (!this._product) return [] as YouTubeVideoModel[];

        return this._product.youTubeVideos
            .filter((youTubeVideo): youTubeVideo is YouTubeVideoType => youTubeVideo !== null)
            .filter((youTubeVideo) => youTubeVideo.privacyStatus === PrivacyStatusType.Public)
            .filter((youTubeVideo) => youTubeVideo.uploadStatus === UploadStatusType.Processed)
            .map(youTubeVideo => new YouTubeVideoModel(youTubeVideo));
    }

    /**
     * @property primaryYouTubeVideos
     * @description - Returns the YouTube videos for the associated primary
     * @returns {YouTubeVideoModel[]}
     */
    public get primaryYouTubeVideos(): YouTubeVideoModel[] {
        if (!this._product) return [] as YouTubeVideoModel[];

        return this._product.primaryYouTubeVideos
            .filter((youTubeVideo): youTubeVideo is YouTubeVideoType => youTubeVideo !== null)
            .filter((youTubeVideo) => youTubeVideo.privacyStatus === PrivacyStatusType.Public)
            .filter((youTubeVideo) => youTubeVideo.uploadStatus === UploadStatusType.Processed)
            .map(youTubeVideo => new YouTubeVideoModel(youTubeVideo));
    }

    /**
     * @property isOnline
     * @description - Returns true if the standalone product is online or if the associated primary and variant are both online
     * @returns {boolean}
     */
    public get isOnline(): boolean {
        return this._product?.isOnline ?? false;
    }

    /**
     * @property isPurchasable
     * @description - Returns true if the product is purchasable
     * @returns {boolean}
     */
    public get isPurchasable(): boolean {
        return this._product?.isPurchasable ?? false;
    }

    /**
     * @property validated
     * @description - Returns true if the standalone product is validated or if the associated primary and variant are both validated
     * @returns {boolean}
     */
    public get validated(): boolean {
        return this._product?.validated ?? false;
    }

    /**
     * @property primaryProductId
     * @description - Returns the primary product id
     * @returns {number | undefined}
     **/
    public get primaryProductId(): number | undefined {
        return this._product?.primaryProductId;
    }
}